import * as React from 'react'
import { Link } from 'react-router'
import { Coins, Loader2 } from 'lucide-react'
import type { OpsBillingDto } from '@lib/dto/billing.dto'
import type { OpsTenantDetailDto } from '@lib/dto/team.dto'
import { formatCents, formatMicros, parseDollarsToCents } from '@lib/helpers/money'
import { grantCreditInputSchema } from '@lib/input/billing.input'
import { ErrorState, LoadingState } from '@/components/PageState'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Field, fieldMessageId } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { toast } from '@/components/ui/toast'
import { apiErrorMessage } from '@/helpers/apiErrorMessage'
import { useOpsBilling, useOpsBillingActions } from '@/hooks/useOpsBilling'

type Actions = ReturnType<typeof useOpsBillingActions>

/** The organization the card is about: only what it shows and checks. */
type BillingTenant = Pick<OpsTenantDetailDto, 'id' | 'name' | 'status'>

interface BillingDialogProps {
  tenant: BillingTenant
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface GrantDialogProps extends BillingDialogProps {
  grant: Actions['grant']
}

interface ExemptionDialogProps extends BillingDialogProps {
  billing: OpsBillingDto
  setExemption: Actions['setExemption']
}

/**
 * Adds credit with a reason the Owners see. One request id per opening of the
 * dialog, so a retried submit can't grant twice.
 */
function GrantDialog({ tenant, open, onOpenChange, grant }: GrantDialogProps) {
  const [amount, setAmount] = React.useState('')
  const [reason, setReason] = React.useState('')
  const [requestId, setRequestId] = React.useState(() => crypto.randomUUID())
  const [errors, setErrors] = React.useState<{ amount?: string; reason?: string; form?: string }>({})
  const cents = parseDollarsToCents(amount)

  function reset(): void {
    setAmount('')
    setReason('')
    setErrors({})
    setRequestId(crypto.randomUUID())
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    const parsed = grantCreditInputSchema.safeParse({ amountCents: cents ?? Number.NaN, reason, requestId })
    if (!parsed.success) {
      const failed = new Set(parsed.error.issues.map((item) => item.path[0]))
      const next: { amount?: string; reason?: string } = {}
      if (cents === null) next.amount = 'Enter an amount in dollars, like 25 or 25.50'
      else if (failed.has('amountCents')) next.amount = 'Between $0.01 and $1,000.00'
      if (failed.has('reason')) next.reason = 'Give a reason'
      setErrors(next)
      return
    }
    setErrors({})
    try {
      await grant.mutateAsync(parsed.data)
      toast.success(`Granted ${formatCents(parsed.data.amountCents)} to ${tenant.name}`)
      reset()
      onOpenChange(false)
    } catch (failure) {
      setErrors({ form: apiErrorMessage(failure, 'Could not grant the credit.') })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <DialogHeader>
            <DialogTitle>Grant credit to {tenant.name}</DialogTitle>
            <DialogDescription>
              Granted credit is spent before paid credit and is never refunded. Use it for pilots and corrections.
            </DialogDescription>
          </DialogHeader>
          <Field label="Amount" htmlFor="grant-amount" error={errors.amount ?? null} hint="In dollars, up to $1,000.00.">
            <Input
              id="grant-amount"
              inputMode="decimal"
              value={amount}
              aria-invalid={errors.amount ? true : undefined}
              aria-describedby={fieldMessageId('grant-amount')}
              onChange={(event) => setAmount(event.target.value)}
            />
          </Field>
          <Field
            label="Reason"
            htmlFor="grant-reason"
            error={errors.reason ?? null}
            hint="Owners see this in an email and in their billing history."
          >
            <Textarea
              id="grant-reason"
              rows={3}
              value={reason}
              aria-invalid={errors.reason ? true : undefined}
              aria-describedby={fieldMessageId('grant-reason')}
              onChange={(event) => setReason(event.target.value)}
            />
          </Field>
          {errors.form ? <p className="text-body text-danger">{errors.form}</p> : null}
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={grant.isPending}>
              {grant.isPending ? <Loader2 className="animate-spin" /> : null}
              {cents !== null && cents > 0 ? `Grant ${formatCents(cents)}` : 'Grant credit'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/** Confirms starting or stopping billing, saying what changes for the organization's live sends. */
function ExemptionDialog({ tenant, billing, open, onOpenChange, setExemption }: ExemptionDialogProps) {
  const [error, setError] = React.useState<string | null>(null)
  const stopping = !billing.exempt

  async function confirm(): Promise<void> {
    setError(null)
    try {
      await setExemption.mutateAsync(stopping)
      toast.success(stopping ? `Stopped billing ${tenant.name}` : `Started billing ${tenant.name}`)
      onOpenChange(false)
    } catch (failure) {
      setError(apiErrorMessage(failure, 'Could not change billing.'))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{stopping ? `Stop billing ${tenant.name}?` : `Start billing ${tenant.name}?`}</DialogTitle>
          <DialogDescription>
            {stopping
              ? 'Their live sends are recorded at $0.00 from now on. Their credit stays where it is.'
              : `Each live send will cost ${formatMicros(billing.priceMicros)} from their credit. Their balance is ${formatMicros(billing.balanceMicros)}, and a send with too little is refused.`}
          </DialogDescription>
        </DialogHeader>
        {error ? <p className="text-body text-danger">{error}</p> : null}
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => void confirm()} disabled={setExemption.isPending}>
            {setExemption.isPending ? <Loader2 className="animate-spin" /> : null}
            {stopping ? 'Stop billing' : 'Start billing'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface OpsBillingCardProps {
  tenant: BillingTenant
  isAdmin: boolean
}

/** An organization's balance and whether it's billed, for staff. ADMIN grants credit and starts or stops billing. */
export function OpsBillingCard({ tenant, isAdmin }: OpsBillingCardProps) {
  const query = useOpsBilling(tenant.id)
  const actions = useOpsBillingActions(tenant.id)
  const [dialog, setDialog] = React.useState<'grant' | 'exemption' | null>(null)
  const canChange = isAdmin && tenant.status !== 'closed'

  function renderBody(billing: OpsBillingDto) {
    return (
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-1">
          <p className="text-caption text-muted-foreground">Balance</p>
          <p className="text-heading font-semibold tabular-nums">{formatMicros(billing.balanceMicros)}</p>
          <p className="text-caption text-muted-foreground tabular-nums">
            {formatMicros(billing.grantedMicros)} granted · {formatMicros(billing.paidMicros)} paid
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canChange ? (
            <>
              <Button variant="secondary" size="sm" onClick={() => setDialog('grant')}>
                Grant credit
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setDialog('exemption')}>
                {billing.exempt ? 'Start billing' : 'Stop billing'}
              </Button>
            </>
          ) : null}
          <Button variant="link" size="sm" asChild>
            <Link to={`/ops/tenants/${tenant.id}/view/billing`}>Open billing</Link>
          </Button>
        </div>
      </div>
    )
  }

  function renderContent() {
    if (query.isLoading) return <LoadingState />
    if (query.isError || !query.data) return <ErrorState message={apiErrorMessage(query.error, 'Could not load billing.')} />
    return renderBody(query.data)
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Coins className="size-4 text-muted-foreground" />
          Billing
          {query.data ? <Badge tone={query.data.exempt ? 'neutral' : 'info'}>{query.data.exempt ? 'Not billed' : 'Billed'}</Badge> : null}
        </CardTitle>
        <CardDescription>Prepaid credit for live envelopes.</CardDescription>
      </CardHeader>
      <CardContent>{renderContent()}</CardContent>
      {canChange && query.data ? (
        <>
          <GrantDialog tenant={tenant} open={dialog === 'grant'} onOpenChange={(open) => setDialog(open ? 'grant' : null)} grant={actions.grant} />
          <ExemptionDialog
            tenant={tenant}
            billing={query.data}
            open={dialog === 'exemption'}
            onOpenChange={(open) => setDialog(open ? 'exemption' : null)}
            setExemption={actions.setExemption}
          />
        </>
      ) : null}
    </Card>
  )
}
